import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Box } from '@mui/material';
import SignaturePad from 'signature_pad';

const SignatureCapture = forwardRef(
  ({ penColor = '#0C5085', backgroundColor = '#ffffff', height = 200, onChange }, ref) => {
    const canvasRef = useRef(null);
    const signaturePadRef = useRef(null);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      const resizeCanvas = () => {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const parentWidth = canvas.parentElement ? canvas.parentElement.offsetWidth : 600;
        canvas.width = parentWidth * ratio;
        canvas.height = height * ratio;
        canvas.getContext('2d').scale(ratio, ratio);
      };

      resizeCanvas();

      const signaturePad = new SignaturePad(canvas, {
        penColor,
        backgroundColor
      });

      signaturePad.onEnd = () => {
        const data = signaturePad.isEmpty() ? null : signaturePad.toDataURL('image/png');
        onChange?.(data);
      };

      signaturePadRef.current = signaturePad;

      window.addEventListener('resize', resizeCanvas);
      return () => {
        window.removeEventListener('resize', resizeCanvas);
        signaturePad.off();
        signaturePadRef.current = null;
      };
    }, [penColor, backgroundColor, height, onChange]);

    useImperativeHandle(ref, () => ({
      clear: () => signaturePadRef.current?.clear(),
      toDataURL: (type = 'image/png') => signaturePadRef.current?.toDataURL(type),
      isEmpty: () => (signaturePadRef.current ? signaturePadRef.current.isEmpty() : true)
    }));

    return (
      <Box
        sx={{
          border: '1px solid rgba(12,80,133,0.3)',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: backgroundColor
        }}
      >
        <canvas ref={canvasRef} style={{ width: '100%', height }} />
      </Box>
    );
  }
);

SignatureCapture.displayName = 'SignatureCapture';

export default SignatureCapture;
